import { StrmClassState } from "explorer/reducers/strm-class";
import { useEffect, useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { RouteComponentProps } from "react-router-dom";
import StrmClassPreview from "shared/assets/images/strm-class-preview.png";
import { ReactComponent as Logo } from "shared/assets/images/logo-strm.svg";
import { ReactComponent as PlayIcon } from "shared/assets/images/play-icon.svg";
import { StrmClassVideo } from "shared/interfaces/StrmClassVideo";
import { Typography } from "antd";
import { isDesktop } from "react-device-detect";
import { ArtistState } from "explorer/reducers/artistas";
import { Loader } from "shared/components";
import useSubscription, {
  SubscriptionPlan,
} from "shared/hooks/useSubscription";
import "./StrmClass.scss";
import SubscriptionDefaultCTA from "shared/components/subscription/SubscriptionDefaultCTA";

const StrmClass: React.FC<RouteComponentProps> = (props) => {
  const { history } = props;
  const { t, i18n } = useTranslation();
  const { currentPlan } = useSubscription();
  const [stageVideos, setStageVideos] = useState<
    {
      stage: number;
      videos: StrmClassVideo[];
    }[]
  >([]);
  const { videos } = useSelector((state: StrmClassState) => state.strmClass);
  const { userArtist } = useSelector((state: ArtistState) => state.artists);
  const stage = Math.min(userArtist?.stage ?? 0, 9);

  const highlightVideo = useMemo(() => {
    const currentStageVideos =
      stageVideos.find((stageVideo) => stageVideo.stage === stage)?.videos ||
      [];
    return currentStageVideos[0];
  }, [stage, stageVideos]);

  let { titulo: highlightVideoTitle } = t(
    `pages.EstagioArtista.estagio.${stage || 0}`
  ) as any;

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    const container = e.currentTarget;
    container.dataset.dragging = "true";
    container.dataset.startX = e.pageX.toString();
    container.dataset.scrollLeft = container.scrollLeft.toString();
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const container = e.currentTarget;
    if (container.dataset.dragging === "true") {
      const startX = parseFloat(container.dataset.startX || "0");
      const scrollLeft = parseFloat(container.dataset.scrollLeft || "0");
      const x = e.pageX - startX;
      container.scrollLeft = scrollLeft - x;
    }
  };

  const handleMouseUp = (e: React.MouseEvent<HTMLDivElement>) => {
    const container = e.currentTarget;
    container.dataset.dragging = "false";
  };

  useEffect(() => {
    if (stage) {
      const languageFiltered = videos.filter((v) =>
        i18n.language === "pt"
          ? v.language === i18n.language
          : v.language === "en"
      );

      const stages = Array.from({ length: stage }, (_, i) => i + 1).reverse();
      const stageVideosList = stages.map((stageNumber) => ({
        stage: stageNumber,
        videos: languageFiltered.filter((v) =>
          v.tags.includes(`stage-${stageNumber}`)
        ),
      }));
      setStageVideos(stageVideosList);
    }
  }, [stage, i18n.language, videos]);

  if (!stage) return <Loader.Simple size="large" fontSize={72} />;

  return (
    <div className={`strm-class ${isDesktop ? "web" : ""}`}>
      <div className="floating-logo">
        <Logo /> class
      </div>
      <div className="highlight-container">
        <div
          className="background"
          style={{
            backgroundImage: `url(${
              highlightVideo?.thumbnailUrl || StrmClassPreview
            })`,
          }}
        />
        <div className="content">
          <Typography.Title level={2} className="title">
            {`${t("pages.StrmClass.stage", {
              stage: stage,
            })} - ${highlightVideoTitle}`}
          </Typography.Title>
          <p>{highlightVideo?.subtitle}</p>
          <button
            className="botao-roxo"
            onClick={() => history.push(`/strm-class/${highlightVideo.id}`)}
          >
            {t("pages.StrmClass.assistir")} <PlayIcon />
          </button>
        </div>
      </div>

      {stageVideos.map((stageVideos) => {
        let { titulo } = t(
          `pages.EstagioArtista.estagio.${stageVideos.stage || 0}`
        ) as any;
        return (
          <div key={stageVideos.stage} className="stage-container">
            <Typography.Title level={2} className="title">
              {`${t("pages.StrmClass.stage", {
                stage: stageVideos.stage,
              })} - ${titulo}`}
            </Typography.Title>
            <div
              className="videos-container"
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
            >
              {stageVideos.videos.map((video) => (
                <div key={video.id} className="video-card">
                  <div
                    className="background"
                    style={{
                      backgroundImage: `url(${
                        video.thumbnailUrl || StrmClassPreview
                      })`,
                    }}
                  />
                  <div className="container">
                    <Typography.Title
                      level={3}
                      className="title"
                      style={{ margin: 0 }}
                    >
                      {video.title}
                    </Typography.Title>
                    {currentPlan === SubscriptionPlan.START &&
                    !video.videoUrl ? (
                      <SubscriptionDefaultCTA
                        title={
                          <Trans
                            i18nKey={t(
                              "components.Subscription.upgradeFeatureCTATitle"
                            )}
                            components={{
                              span: <span className="cor-destaque" />,
                            }}
                          />
                        }
                      />
                    ) : (
                      <button
                        className="botao-roxo"
                        onClick={() => history.push(`/strm-class/${video.id}`)}
                      >
                        {t("pages.StrmClass.assistir")} <PlayIcon />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default StrmClass;
